import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RivetsApiError, type TaskApproval, type TaskSource } from "@rivets/core";
import { decideTaskApproval, fetchTaskApproval, retryTransient, type TaskApprovalDecision } from "@/lib/api";
import { qk } from "@/lib/query-keys";

/**
 * The human gate in front of integration-sourced work: the pure wording the
 * approval card and its decided banner use, and the two hooks that read and
 * decide it. The prompt itself is never formatted here — it is attacker-
 * controlled text and is only ever rendered as a text node.
 */

/** "Linear", "Slack", or the integration's own kind, capitalised. */
export function approvalSourceName(source: Pick<TaskSource, "kind">): string {
  switch (source.kind) {
    case "linear": return "Linear";
    case "slack": return "Slack";
    default: return source.kind.charAt(0).toUpperCase() + source.kind.slice(1);
  }
}

/** Only an http(s) link is offered: the URL came from outside Rivets too. */
export function approvalSourceUrl(source: Pick<TaskSource, "url">): string | null {
  if (!source.url) return null;
  try {
    const url = new URL(source.url);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

/** "5m ago", "just now", or the date once it is a week old. */
export function approvalAge(timestamp: number, now = Date.now()): string {
  const seconds = Math.max(0, (now - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function approvalRequestedLine(approval: Pick<TaskApproval, "source" | "requestedByName">): string {
  return `Requested via ${approvalSourceName(approval.source)} by ${approval.requestedByName}`;
}

/** The compact record a decided approval leaves behind; null while pending. */
export function approvalDecisionText(approval: Pick<TaskApproval, "status" | "decidedBy" | "decidedByName" | "source" | "requestedByName">): string | null {
  switch (approval.status) {
    case "pending":
      return null;
    case "approved":
      return `Approved by ${approval.decidedByName ?? "a member"} · requested via ${approvalSourceName(approval.source)} by ${approval.requestedByName}`;
    case "rejected":
      // No decider: Rivets withdrew it itself (the issue was unassigned or
      // stopped in Linear before anyone decided).
      return approval.decidedBy === null ? "Rejected automatically" : `Rejected by ${approval.decidedByName ?? "a member"}`;
  }
}

/** A decision somebody else already made: the answer is to refetch, not retry. */
export function approvalAlreadyDecided(error: unknown): boolean {
  return error instanceof RivetsApiError && error.status === 409 && error.code === "approval_decided";
}

/** A fresh id per press. Replaying an id returns the original result, so a
 * second press after a definitive answer must not reuse the first one. */
export function approvalRequestId(): string {
  return crypto.randomUUID();
}

export function useTaskApproval(chatId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.chatApproval(chatId),
    queryFn: ({ signal }) => fetchTaskApproval(chatId, signal),
    enabled,
    retry: retryTransient,
    staleTime: 30_000,
  });
}

export function useDecideTaskApproval(approval: Pick<TaskApproval, "chatId" | "worktreeId" | "orgId" | "kind"> | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decision: TaskApprovalDecision) => {
      if (!approval) throw new Error("This approval is still loading.");
      return decideTaskApproval(approval.chatId, decision, approvalRequestId());
    },
    onSuccess: (decided) => {
      queryClient.setQueryData(qk.chatApproval(decided.chatId), decided);
      // The realtime frames move the task and the chat on; these reads catch a
      // viewer whose socket is between connections.
      void queryClient.invalidateQueries({ queryKey: qk.chat(decided.chatId) });
      void queryClient.invalidateQueries({ queryKey: qk.worktree(decided.worktreeId) });
      // A rejected assistant conversation leaves the assistant's state
      // entirely, and only a refetch says so.
      if (decided.kind === "assistant") void queryClient.invalidateQueries({ queryKey: qk.assistant(decided.orgId) });
    },
    onError: (error) => {
      if (approval && approvalAlreadyDecided(error)) void queryClient.invalidateQueries({ queryKey: qk.chatApproval(approval.chatId) });
    },
  });
}
